export class ScrollController {
  private scrollY: number = 0;
  private normalizedScroll: number = 0;
  private scrollProgress: number = 0;
  private heroHeight: number = 0;
  private scrollHeroHeight: number = 0;
  private callbacks: Array<(progress: number, scrollY: number) => void> = [];
  private scrollHeroCallbacks: Array<(progress: number, scrollY: number) => void> = [];
  private heroScrollDelta: number = 0; // Accumulated scroll for hero animation
  private heroScrollStartY: number = 0; // Hero section start position
  // heroScrollEndY removed - not used // Hero section end position (for reverse scroll)
  private readonly HERO_SCROLL_RANGE_REM: number = 100; // Total scroll needed in rem
  private heroScrollCallbacks: Array<(progress: number, isComplete: boolean) => void> = [];
  private scrollUpdateFrame: number | null = null; // For throttling scroll updates
  private lastScrollToTime: number = 0; // Throttle scrollTo calls
  private smoothedScrollDelta: number = 0; // Smoothed scroll delta for easing
  private lastScrollY: number = 0; // Track last scroll position for direction detection
  private cardSectionStartY: number = 0; // Card section start position
  private cardSectionDelta: number = 0; // Accumulated scroll for card animation
  private readonly CARD_SCROLL_RANGE_REM: number = 100; // Total scroll needed for cards
  private cardScrollCallbacks: Array<(progress: number, isComplete: boolean) => void> = [];
  private cardScrollUpdateFrame: number | null = null; // For throttling card scroll updates
  private lastCardScrollToTime: number = 0; // Throttle card scrollTo calls
  private smoothedCardScrollDelta: number = 0; // Smoothed card scroll delta
  
  // Calculate scroll range in pixels based on rem
  private getHeroScrollRange(): number {
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    return this.HERO_SCROLL_RANGE_REM * rootFontSize;
  }
  
  // Easing function: ease-in-out cubic for smooth transitions
  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  constructor() {
    this.setupScrollListener();
    this.setupHeroScrollPinning();
    this.setupCardScrollPinning();
    this.updateHeroHeight();
    window.addEventListener('resize', () => this.updateHeroHeight());
  }

  private setupHeroScrollPinning(): void {
    const heroSection = document.querySelector('.hero-section') as HTMLElement;
    if (!heroSection) return;

    // Initialize hero scroll positions
    this.heroScrollStartY = heroSection.offsetTop;
    // heroScrollEndY removed - not used
    this.lastScrollY = window.scrollY;

    const handleWheel = (e: WheelEvent): void => {
      const currentScrollY = window.scrollY;
      const scrollRange = this.getHeroScrollRange();
      
      // Detect scroll direction
      const isScrollingDown = currentScrollY > this.lastScrollY || e.deltaY > 0;
      const isScrollingUp = currentScrollY < this.lastScrollY || e.deltaY < 0;
      this.lastScrollY = currentScrollY;
      
      // Check if we're at the hero section start
      // Both directions pin at the START of the hero section
      const distanceFromStart = Math.abs(currentScrollY - this.heroScrollStartY);
      
      // Only pin if:
      // 1. Scrolling down AND at start AND delta hasn't reached max
      // 2. Scrolling up AND at start AND delta hasn't reached 0
      const shouldPinDown = distanceFromStart < 100 && isScrollingDown && this.heroScrollDelta < scrollRange;
      const shouldPinUp = distanceFromStart < 100 && isScrollingUp && this.heroScrollDelta > 0;
      
      // Don't pin if at boundaries - allow normal scrolling
      // Also skip if GSAP ScrollTrigger is handling this section
      const hasGSAPScrollTrigger = (window as any).__gsapScrollTriggerActive;
      if ((shouldPinDown || shouldPinUp) && !hasGSAPScrollTrigger) {
        e.preventDefault();
        e.stopPropagation();
        
        // Accumulate scroll delta with smooth damping
        const scrollDelta = e.deltaY * 0.8;
        
        if (shouldPinDown) {
          // Scrolling down: increase delta (0 → scrollRange)
          this.heroScrollDelta = Math.max(0, Math.min(scrollRange, this.heroScrollDelta + scrollDelta));
        } else if (shouldPinUp) {
          // Scrolling up: decrease delta (scrollRange → 0)
          this.heroScrollDelta = Math.max(0, Math.min(scrollRange, this.heroScrollDelta - Math.abs(scrollDelta)));
        }
        
        // Smooth the scroll delta using easing (ease-in-out)
        // This creates a smoother, more natural feel
        const targetDelta = this.heroScrollDelta;
        const smoothingFactor = 0.2; // Increased for smoother, more responsive feel
        this.smoothedScrollDelta += (targetDelta - this.smoothedScrollDelta) * smoothingFactor;
        
        // Clamp smoothed delta to prevent overshooting
        this.smoothedScrollDelta = Math.max(0, Math.min(scrollRange, this.smoothedScrollDelta));
        
        // Throttle scrollTo calls more aggressively to reduce stuttering and flickering
        // Only pin scroll if delta is within range (not at boundaries)
        // Both directions pin at the START of the hero section
        const now = performance.now();
        const scrollToThrottle = 50; // Increased from 32ms to 50ms for smoother scrolling
        const distanceThreshold = 20; // Increased from 10px to reduce unnecessary calls
        
        if (now - this.lastScrollToTime > scrollToThrottle) {
          // Only pin if delta is actively changing (not at 0 or max)
          if ((shouldPinDown || shouldPinUp) && this.heroScrollDelta > 0 && this.heroScrollDelta < scrollRange && distanceFromStart > distanceThreshold) {
            // Use requestAnimationFrame for scrollTo to sync with browser rendering
            requestAnimationFrame(() => {
              window.scrollTo({ top: this.heroScrollStartY, behavior: 'auto' });
            });
            this.lastScrollToTime = now;
          }
          // Don't call scrollTo when delta is at boundaries - allow natural scroll
        }
        
        // Throttle callback updates using requestAnimationFrame with easing
        if (this.scrollUpdateFrame === null) {
          this.scrollUpdateFrame = requestAnimationFrame(() => {
            // Apply ease-in-out to the progress for smoother animation
            const rawProgress = this.smoothedScrollDelta / scrollRange;
            const easedProgress = this.easeInOutCubic(rawProgress);
            const clampedProgress = Math.max(0, Math.min(1, easedProgress));
            const isComplete = clampedProgress >= 1.0;
            
            this.heroScrollCallbacks.forEach(cb => cb(clampedProgress, isComplete));
            this.scrollUpdateFrame = null;
          });
        }
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false, capture: true });

    // Touch events for mobile
    let touchStartY = 0;
    window.addEventListener('touchstart', (e: TouchEvent) => {
      if (e.touches.length > 0) {
        touchStartY = e.touches[0].clientY;
      }
    }, { passive: false });

    window.addEventListener('touchmove', (e: TouchEvent) => {
      const currentScrollY = window.scrollY;
      const scrollRange = this.getHeroScrollRange();
      
      // Detect scroll direction
      const isScrollingDown = currentScrollY > this.lastScrollY;
      const isScrollingUp = currentScrollY < this.lastScrollY;
      this.lastScrollY = currentScrollY;
      
      const distanceFromStart = Math.abs(currentScrollY - this.heroScrollStartY);
      
      // Only pin if delta is within range
      // Both directions pin at the START of the hero section
      const shouldPinDown = distanceFromStart < 100 && isScrollingDown && this.heroScrollDelta < scrollRange && e.touches.length > 0;
      const shouldPinUp = distanceFromStart < 100 && isScrollingUp && this.heroScrollDelta > 0 && e.touches.length > 0;
      
      // Skip if GSAP ScrollTrigger is handling this section
      const hasGSAPScrollTrigger = (window as any).__gsapScrollTriggerActive;
      if ((shouldPinDown || shouldPinUp) && !hasGSAPScrollTrigger) {
        e.preventDefault();
        const delta = (touchStartY - e.touches[0].clientY) * 0.8; // Damping for smoothness
        touchStartY = e.touches[0].clientY;
        
        if (shouldPinDown) {
          // Scrolling down: increase delta
          this.heroScrollDelta = Math.max(0, Math.min(scrollRange, this.heroScrollDelta + delta));
        } else if (shouldPinUp) {
          // Scrolling up: decrease delta
          this.heroScrollDelta = Math.max(0, Math.min(scrollRange, this.heroScrollDelta - Math.abs(delta)));
        }
        
        // Smooth the scroll delta using easing
        const targetDelta = this.heroScrollDelta;
        const smoothingFactor = 0.15;
        this.smoothedScrollDelta += (targetDelta - this.smoothedScrollDelta) * smoothingFactor;
        
        // Clamp smoothed delta to prevent overshooting
        this.smoothedScrollDelta = Math.max(0, Math.min(scrollRange, this.smoothedScrollDelta));
        
        // Throttle scrollTo calls more aggressively
        // Only pin scroll if delta is within range (not at boundaries)
        // Both directions pin at the START of the hero section
        const now = performance.now();
        if (now - this.lastScrollToTime > 32) {
          if ((shouldPinDown || shouldPinUp) && this.heroScrollDelta > 0 && this.heroScrollDelta < scrollRange && distanceFromStart > 10) {
            window.scrollTo({ top: this.heroScrollStartY, behavior: 'auto' });
            this.lastScrollToTime = now;
          }
        }
        
        // Throttle callback updates with easing
        if (this.scrollUpdateFrame === null) {
          this.scrollUpdateFrame = requestAnimationFrame(() => {
            const rawProgress = this.smoothedScrollDelta / scrollRange;
            const easedProgress = this.easeInOutCubic(rawProgress);
            const clampedProgress = Math.max(0, Math.min(1, easedProgress));
            this.heroScrollCallbacks.forEach(cb => cb(clampedProgress, clampedProgress >= 1.0));
            this.scrollUpdateFrame = null;
          });
        }
      }
    }, { passive: false });

    // Update hero positions
    const updatePositions = (): void => {
      if (heroSection) {
        const section = heroSection as HTMLElement;
        this.heroScrollStartY = section.offsetTop;
      }
    };
    updatePositions();
    window.addEventListener('scroll', updatePositions, { passive: true });
  }

  private setupCardScrollPinning(): void {
    const cardSection = document.querySelector('.how-to-grow-section') as HTMLElement;
    if (!cardSection) return;

    // Initialize card section start position
    this.cardSectionStartY = cardSection.offsetTop;
    
    // Update position on scroll/resize
    const updateCardStart = (): void => {
      if (cardSection) {
        this.cardSectionStartY = (cardSection as HTMLElement).offsetTop;
      }
    };
    updateCardStart();
    window.addEventListener('scroll', updateCardStart, { passive: true });
    window.addEventListener('resize', updateCardStart);

    // Calculate initial progress based on current scroll position
    const calculateInitialProgress = (): number => {
      const currentScrollY = window.scrollY;
      const scrollRange = this.getCardScrollRange();
      const sectionTop = this.cardSectionStartY;
      const viewportHeight = window.innerHeight;
      
      // Calculate how far into the section the user has scrolled
      // Progress starts when section enters viewport (section top reaches viewport bottom)
      const sectionEnterPoint = sectionTop - viewportHeight;
      const scrollIntoSection = Math.max(0, currentScrollY - sectionEnterPoint);
      
      // Map scroll distance to progress (0-1)
      // Use a reasonable range for initial calculation (e.g., 2x viewport height)
      const initialRange = viewportHeight * 2;
      const initialProgress = Math.min(1, scrollIntoSection / initialRange);
      
      // Convert progress to delta
      return initialProgress * scrollRange;
    };

    const handleWheel = (e: WheelEvent): void => {
      const currentScrollY = window.scrollY;
      const scrollRange = this.getCardScrollRange();
      
      // Detect scroll direction
      const isScrollingDown = currentScrollY > this.lastScrollY || e.deltaY > 0;
      const isScrollingUp = currentScrollY < this.lastScrollY || e.deltaY < 0;
      this.lastScrollY = currentScrollY;
      
      // Check if we're at the card section start
      const distanceFromStart = Math.abs(currentScrollY - this.cardSectionStartY);
      const isNearSection = distanceFromStart < 100;
      const shouldPinDown = isNearSection && isScrollingDown && this.cardSectionDelta < scrollRange;
      const shouldPinUp = isNearSection && isScrollingUp && this.cardSectionDelta > 0;
      
      // Initialize delta from current scroll position if entering pinned state (first time)
      if (shouldPinDown && this.cardSectionDelta === 0) {
        // User is entering the pinned section - initialize from current scroll position
        const initialDelta = calculateInitialProgress();
        this.cardSectionDelta = Math.max(0, Math.min(scrollRange, initialDelta));
        this.smoothedCardScrollDelta = this.cardSectionDelta;
      }
      
      // Reset delta when scrolling away from section
      if (!isNearSection && this.cardSectionDelta > 0) {
        this.cardSectionDelta = 0;
        this.smoothedCardScrollDelta = 0;
      }
      
      // Both directions pin at the START of the card section
      if (shouldPinDown || shouldPinUp) {
        e.preventDefault();
        e.stopPropagation();
        
        // Accumulate scroll delta with smooth damping
        const scrollDelta = e.deltaY * 0.8;
        
        if (shouldPinDown) {
          // Scrolling down: increase delta (0 → scrollRange)
          this.cardSectionDelta = Math.max(0, Math.min(scrollRange, this.cardSectionDelta + scrollDelta));
        } else if (shouldPinUp) {
          // Scrolling up: decrease delta (scrollRange → 0)
          this.cardSectionDelta = Math.max(0, Math.min(scrollRange, this.cardSectionDelta - Math.abs(scrollDelta)));
        }
        
        // Smooth the scroll delta using easing with better smoothing
        const targetDelta = this.cardSectionDelta;
        const smoothingFactor = 0.2; // Increased for smoother, more responsive feel
        this.smoothedCardScrollDelta += (targetDelta - this.smoothedCardScrollDelta) * smoothingFactor;
        this.smoothedCardScrollDelta = Math.max(0, Math.min(scrollRange, this.smoothedCardScrollDelta));
        
        // Throttle scrollTo calls more aggressively to prevent flickering
        const now = performance.now();
        const scrollToThrottle = 50; // Increased from 32ms to 50ms for smoother scrolling
        const distanceThreshold = 20; // Increased from 10px to reduce unnecessary calls
        
        if (now - this.lastCardScrollToTime > scrollToThrottle) {
          // Only call scrollTo if we're significantly away from the pinned position
          if ((shouldPinDown || shouldPinUp) && this.cardSectionDelta > 0 && this.cardSectionDelta < scrollRange && distanceFromStart > distanceThreshold) {
            // Use requestAnimationFrame for scrollTo to sync with browser rendering
            requestAnimationFrame(() => {
              window.scrollTo({ top: this.cardSectionStartY, behavior: 'auto' });
            });
            this.lastCardScrollToTime = now;
          }
        }
        
        // Throttle callback updates with easing - ensure smooth animation updates
        if (this.cardScrollUpdateFrame === null) {
          this.cardScrollUpdateFrame = requestAnimationFrame(() => {
            const rawProgress = this.smoothedCardScrollDelta / scrollRange;
            const easedProgress = this.easeInOutCubic(rawProgress);
            const clampedProgress = Math.max(0, Math.min(1, easedProgress));
            
            // Always update callbacks - this ensures cards update smoothly
            this.cardScrollCallbacks.forEach(cb => cb(clampedProgress, clampedProgress >= 1.0));
            this.cardScrollUpdateFrame = null;
          });
        }
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false, capture: true });
  }

  private getCardScrollRange(): number {
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    return this.CARD_SCROLL_RANGE_REM * rootFontSize;
  }

  public onCardScroll(callback: (progress: number, isComplete: boolean) => void): void {
    this.cardScrollCallbacks.push(callback);
    const scrollRange = this.getCardScrollRange();
    const currentProgress = Math.min(this.cardSectionDelta / scrollRange, 1);
    callback(currentProgress, currentProgress >= 1.0);
  }

  private setupScrollListener(): void {
    let ticking = false;

    const handleScroll = (): void => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Check if hero scroll is pinned
          const heroSection = document.querySelector('.hero-section') as HTMLElement;
          const cardSection = document.querySelector('.how-to-grow-section') as HTMLElement;
          const currentScrollY = window.scrollY;
          
          if (heroSection) {
            const scrollRange = this.getHeroScrollRange();
            const distanceFromStart = Math.abs(currentScrollY - this.heroScrollStartY);
            
            // Only pin if delta is within range (not at boundaries 0 or scrollRange)
            // Both directions pin at the START of the hero section
            const isHeroPinned = distanceFromStart < 100 && this.heroScrollDelta > 0 && this.heroScrollDelta < scrollRange;
            
            if (isHeroPinned) {
              // Keep scroll pinned at start (for both directions)
              const now = performance.now();
              const scrollToThrottle = 50;
              const distanceThreshold = 20;
              if (now - this.lastScrollToTime > scrollToThrottle && distanceFromStart > distanceThreshold) {
                requestAnimationFrame(() => {
                  window.scrollTo({ top: this.heroScrollStartY, behavior: 'auto' });
                });
                this.lastScrollToTime = now;
              }
              this.scrollY = this.heroScrollStartY;
            } else if (cardSection) {
              // Check if card section is pinned
              const cardScrollRange = this.getCardScrollRange();
              const cardDistanceFromStart = Math.abs(currentScrollY - this.cardSectionStartY);
              const isCardPinned = cardDistanceFromStart < 100 && this.cardSectionDelta > 0 && this.cardSectionDelta < cardScrollRange;
              
              if (isCardPinned) {
                // Keep scroll pinned at card section start
                const now = performance.now();
                const scrollToThrottle = 50;
                const distanceThreshold = 20;
                if (now - this.lastCardScrollToTime > scrollToThrottle && cardDistanceFromStart > distanceThreshold) {
                  requestAnimationFrame(() => {
                    window.scrollTo({ top: this.cardSectionStartY, behavior: 'auto' });
                  });
                  this.lastCardScrollToTime = now;
                }
                this.scrollY = this.cardSectionStartY;
              } else {
                // Reset card delta when not pinned
                if (cardDistanceFromStart > 100 && this.cardSectionDelta > 0) {
                  this.cardSectionDelta = 0;
                  this.smoothedCardScrollDelta = 0;
                }
                this.scrollY = window.scrollY;
              }
            } else {
              this.scrollY = window.scrollY;
            }
          } else {
            this.scrollY = window.scrollY;
          }
          
          this.updateScrollProgress();
          this.updateScrollHeroProgress();
          this.notifyCallbacks();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    this.updateScrollProgress();
    this.updateScrollHeroProgress();
  }

  private updateHeroHeight(): void {
    const heroSection = document.querySelector('.hero-section');
    if (heroSection) {
      this.heroHeight = heroSection.clientHeight;
    }
    
    const scrollHeroSection = document.querySelector('.scroll-hero-section');
    if (scrollHeroSection) {
      this.scrollHeroHeight = scrollHeroSection.clientHeight;
    }
  }

  private updateScrollProgress(): void {
    // Normalize scroll progress (0 to 1). When no hero section, use doc height so progress advances with scroll.
    if (this.heroHeight <= 0) {
      const maxScroll = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
      this.normalizedScroll = maxScroll > 0 ? Math.min(this.scrollY / maxScroll, 1) : 1;
      this.scrollProgress = this.normalizedScroll;
      return;
    }
    this.normalizedScroll = Math.min(this.scrollY / this.heroHeight, 1);
    this.scrollProgress = this.normalizedScroll;
  }

  private updateScrollHeroProgress(): void {
    // Calculate progress within the scroll hero section
    // Progress goes from 0 (at top) to 1 (at bottom of scroll hero section)
    const scrollHeroProgress = Math.min(this.scrollY / this.scrollHeroHeight, 1);
    
    // Notify scroll hero callbacks
    this.scrollHeroCallbacks.forEach((callback) => {
      callback(scrollHeroProgress, this.scrollY);
    });
  }

  public onScrollHero(callback: (progress: number, scrollY: number) => void): void {
    this.scrollHeroCallbacks.push(callback);
    // Immediately call with current progress
    const currentProgress = Math.min(this.scrollY / this.scrollHeroHeight, 1);
    callback(currentProgress, this.scrollY);
  }

  private notifyCallbacks(): void {
    this.callbacks.forEach((callback) => {
      callback(this.scrollProgress, this.scrollY);
    });
  }

  public onScroll(callback: (progress: number, scrollY: number) => void): void {
    this.callbacks.push(callback);
  }

  public getScrollProgress(): number {
    return this.scrollProgress;
  }

  public getNormalizedScroll(): number {
    return this.normalizedScroll;
  }

  public getScrollY(): number {
    return this.scrollY;
  }

  public onHeroScroll(callback: (progress: number, isComplete: boolean) => void): void {
    console.log('✅ onHeroScroll callback registered');
    this.heroScrollCallbacks.push(callback);
    const scrollRange = this.getHeroScrollRange();
    const currentProgress = Math.min(this.heroScrollDelta / scrollRange, 1);
    console.log(`📊 Initial hero scroll progress: ${(currentProgress * 100).toFixed(1)}% (${this.HERO_SCROLL_RANGE_REM}rem range)`);
    callback(currentProgress, currentProgress >= 1.0);
  }

  public dispose(): void {
    this.callbacks = [];
    this.scrollHeroCallbacks = [];
    this.heroScrollCallbacks = [];
    this.cardScrollCallbacks = [];
    if (this.scrollUpdateFrame !== null) {
      cancelAnimationFrame(this.scrollUpdateFrame);
      this.scrollUpdateFrame = null;
    }
    if (this.cardScrollUpdateFrame !== null) {
      cancelAnimationFrame(this.cardScrollUpdateFrame);
      this.cardScrollUpdateFrame = null;
    }
  }
}
